import { useCallback, useEffect, useRef, useState } from 'react'
import type { SharedRoomOfferState, SharedRoomDismissedMatch } from './useSharedRoomOffer'

export type SharedRoomHeaderVariant = 'none' | 'similarDetected' | 'inSharedRoom'

export type MeetingTransientToastState = {
  id: number
  kind: 'success' | 'error'
  message: string
}

const TOAST_MS = 10_000

export function useSharedRoomUi(input: {
  offerState: SharedRoomOfferState
  lastDismissedMatch: SharedRoomDismissedMatch | null
  leftSharedRoom: boolean
  inSharedRoom: boolean
}) {
  const { offerState, lastDismissedMatch, inSharedRoom } = input

  const [detectionModalOpen, setDetectionModalOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [toast, setToast] = useState<MeetingTransientToastState | null>(null)
  const openedMatchIdRef = useRef<string | null>(null)
  const reopenFromJoinRef = useRef(false)
  const prevOfferKindRef = useRef<SharedRoomOfferState['kind']>('idle')

  const showToast = useCallback((message: string, kind: MeetingTransientToastState['kind'] = 'success') => {
    setToast({ id: Date.now(), kind, message })
  }, [])

  const dismissToast = useCallback(() => setToast(null), [])

  useEffect(() => {
    if (!toast) return undefined
    const timer = window.setTimeout(() => {
      setToast((current) => (current?.id === toast.id ? null : current))
    }, TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (offerState.kind === 'offered' || offerState.kind === 'requested') {
      if (openedMatchIdRef.current !== offerState.matchId) {
        openedMatchIdRef.current = offerState.matchId
        setDetectionModalOpen(true)
      }
    }
    const prevKind = prevOfferKindRef.current
    if (offerState.kind === 'waiting' && prevKind !== 'waiting') {
      setDetectionModalOpen(false)
    }
    if (offerState.kind === 'joined' && prevKind !== 'joined') {
      setDetectionModalOpen(false)
      openedMatchIdRef.current = null
    }
    if (offerState.kind === 'closed' && prevKind !== 'closed') {
      setDetectionModalOpen(false)
    }
    prevOfferKindRef.current = offerState.kind
  }, [offerState, inSharedRoom])

  const openDetectionModal = useCallback(() => {
    if (!lastDismissedMatch && offerState.kind !== 'offered' && offerState.kind !== 'requested') {
      return
    }
    reopenFromJoinRef.current = true
    setDetectionModalOpen(true)
  }, [lastDismissedMatch, offerState.kind])

  const closeDetectionModal = useCallback(() => {
    reopenFromJoinRef.current = false
    setDetectionModalOpen(false)
  }, [])

  const openLeaveConfirm = useCallback(() => setLeaveConfirmOpen(true), [])
  const closeLeaveConfirm = useCallback(() => setLeaveConfirmOpen(false), [])

  const snapshotRetryVisible =
    Boolean(lastDismissedMatch) && reopenFromJoinRef.current

  const detectionModalVisible =
    detectionModalOpen &&
    (offerState.kind === 'offered' ||
      offerState.kind === 'requested' ||
      offerState.kind === 'waiting' ||
      snapshotRetryVisible)

  // Similar · Join: decline (No), leave room, or merge failed — all via lastDismissedMatch.
  const headerVariant: SharedRoomHeaderVariant = inSharedRoom
    ? 'inSharedRoom'
    : lastDismissedMatch && !detectionModalVisible
      ? 'similarDetected'
      : 'none'

  return {
    headerVariant,
    detectionModalVisible,
    leaveConfirmOpen,
    toast,
    showToast,
    dismissToast,
    openDetectionModal,
    closeDetectionModal,
    openLeaveConfirm,
    closeLeaveConfirm,
  }
}
